/**
 * Phone game detail V2 — `design/Games Tonight Mobile.dc.html` :532-628.
 *
 * THE PHONE HAS NO INLINE PARTY PANEL AT ANY WIDTH. A control ON the top bar
 * opens `sheets/FriendsSheet.tsx`, a full-screen view with its own 56px header,
 * and that is where the roster, the call strip, the comments and the composer
 * live now. This screen is the game; the sheet is the crew.
 *
 * FULL SCREEN, like the round page: the shell hides its header and tab bar for
 * this screen (`headerVM().hdrOn`), and the sticky bar below carries both the
 * way out and the way to the crew. Both trees make the same move on this
 * screen; the phone's bar copies `screens/Round.tsx`'s, down to the 44px tap
 * targets and the right-hand control slot.
 *
 * NOTHING ON THIS SCREEN IS ABSOLUTELY POSITIONED AGAINST THE BODY any more,
 * and the body's `position:relative` went with it. The FRIENDS control was the
 * only thing that needed it: it used to be a tab pinned at `top:0`, tucked
 * under the z40 sticky header. With the header gone there was nothing left to
 * tuck under, so it moved onto the bar instead. The cover's ribbons resolve
 * against the cover's own box and never needed this one.
 *
 * Order changed from the old screen: the title block moves ABOVE the cover, and
 * the five stacked lines it used to carry (dev / meta / review / PEAK / price)
 * collapse into one meta line plus the ledger beside the cover. Price, players,
 * release and peak are facts about the game and belong in the ledger; who made
 * it, where it runs and how it reviews are the pitch.
 *
 * WHAT V2 CUTS FROM THIS SCREEN, and where it went:
 *   · the three status pills — the cover gains `Ribbon`, which it never had
 *   · the standalone PEAK line — the ledger's PEAK row
 *   · the tag chip row — the ledger's TAGS line
 *   · the `DATA:` chip row — cut outright
 *   · YOUR CALL — the sheet's call strip (leaving it here would ship two call
 *     controls on one screen)
 *   · FRIENDS' INTEREST, COMMENTS and the composer — the sheet
 *   · the bottom "add it to vote and comment" note — the sheet's not-on-list
 *     state carries the same promise, next to the target that acts on it
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { Bell } from '../components/Bell.tsx';
import { DoorIcon } from '../components/DoorIcon.tsx';
import { MediaCarousel } from '../components/MediaCarousel.tsx';
import { PixelTriangle } from '../components/PixelTriangle.tsx';
import { Ribbon } from '../components/Ribbon.tsx';
import { useArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { NO_STEAM_TIP, quickAddBlocked } from '../store/selectors.3c.ts';
import { detailVM, headerVM } from '../store/selectors.ts';
import type { DetailVM } from '../store/selectors.ts';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/** Ledger rows — every one but TAGS, which has nothing under it to rule off. */
const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  padding: '9px 11px',
  borderBottom: '1px solid var(--gt-line)',
};

const LABEL: CSSProperties = {
  flex: 1,
  fontFamily: MONO,
  fontSize: 8,
  letterSpacing: '.1em',
  color: 'var(--gt-muted)',
};

/** Full-width stat rows — the phone band is rows where desktop is columns. */
const STAT_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 9,
  padding: '11px 0',
  borderBottom: '1px solid var(--gt-line)',
};

const CHIP: CSSProperties = {
  fontFamily: MONO,
  fontSize: 9,
  letterSpacing: '.06em',
  padding: '4px 5px',
};

/**
 * The full-screen top bar, geometry copied from the round page's own
 * (`screens/Round.tsx`) so the two screens that hide the phone's chrome look
 * like siblings rather than two separate ideas about what replaces it.
 *
 * `sticky` for the same reason the header it replaces was: this page scrolls
 * the document, and an exit that scrolls away on a long detail page is an exit
 * you have to scroll back up to find. That now goes double — the crew is up
 * here too. `z:20` is Round's, and it keeps a scrolled page passing UNDER the
 * bar rather than through it.
 *
 * THE BACK CONTROL KEEPS ITS BORDER AND ITS 44px, which is where the phone
 * parts company with the desktop bar's bare text: this one is a thumb target,
 * and 44 is the number the rest of the phone tree uses for one.
 *
 * `right` is the FRIENDS toggle, and the bar takes it as a node rather than as
 * `d` because the empty-game branch renders this bar with no game to build one
 * from. Round puts its own right-hand control (LEAVE ROUND) in the same slot.
 */
function DetailTopBar({
  backTxt,
  onBack,
  right,
}: {
  backTxt: string;
  onBack: () => void;
  right?: JSX.Element;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 14px',
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-panel)',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <div
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 44,
          padding: '0 11px',
          border: '1px solid var(--gt-border-2)',
          color: 'var(--gt-text-4)',
          fontFamily: MONO,
          fontSize: '9.5px',
          letterSpacing: '.05em',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          cursor: 'pointer',
          flex: 'none',
        }}
      >
        ◂ {backTxt}
      </div>
      {right ? <div style={{ marginLeft: 'auto', flex: 'none' }}>{right}</div> : null}
    </div>
  );
}

/**
 * The FRIENDS toggle, in the bar since the phone's chrome went away.
 *
 * It used to hang off the top-right corner of the content, absolutely
 * positioned with `borderTop:0; borderRight:0` so it read as a tab tucked under
 * the sticky header. There is no header to tuck under now, and a tab hanging
 * off nothing is just a control that has lost its edge — so it becomes an
 * ordinary bordered control on the bar's right, opposite the way out. Round's
 * LEAVE ROUND sits in the same slot.
 *
 * 44 tall to match the back control, so the bar reads as one row rather than
 * two controls that happen to share a strip. The badge keeps its own colour:
 * it is the crew's call on this game, and the one thing on the bar that is
 * about the game rather than about navigation.
 *
 * BOTH STATES SURVIVE THE MOVE, including the tip that explains a blocked add
 * rather than inviting one — the mirror tips this `NOT ON TONIGHT'S LIST — ADD
 * IT` unconditionally, which on a CheapShark-only row invites a press that
 * opens a panel whose only control is inert.
 */
function FriendsToggle({
  d,
  addBlocked,
  onOpen,
}: {
  d: DetailVM;
  addBlocked: boolean;
  onOpen: () => void;
}): JSX.Element {
  const box: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    boxSizing: 'border-box',
    background: 'var(--gt-panel)',
    cursor: 'pointer',
  };
  return d.inList ? (
    <div
      onClick={onOpen}
      data-tip={'PARTY · ' + d.calledTxt}
      style={{ ...box, gap: 9, padding: '0 10px', border: '1px solid var(--gt-border)' }}
    >
      <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-text-3)' }}>
        PARTY
      </span>
      <span
        style={{
          fontFamily: 'var(--gt-font-pixel)',
          fontSize: 12,
          background: d.groupBg,
          color: 'var(--gt-ink)',
          padding: '7px 8px',
          lineHeight: 1,
        }}
      >
        <span style={{ display: 'block', transform: 'translateY(0.13em)' }}>{d.groupBig}</span>
      </span>
    </div>
  ) : (
    // Off the list the button is still PARTY (2026-09-03): who owns it is a
    // fact about the game, and the sheet answers it. Adding lives in the body.
    <div
      onClick={onOpen}
      data-tip={addBlocked ? NO_STEAM_TIP : "NOT ON TONIGHT'S LIST — SEE WHO OWNS IT"}
      style={{ ...box, padding: '0 12px', border: '1px solid var(--gt-border)' }}
    >
      <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-text-3)' }}>PARTY</span>
    </div>
  );
}

export default function Detail(): JSX.Element {
  const s = useQd();
  const d = detailVM(s, s.detailId);
  const art = useArt(s.detailId + 'p', d ? d.coverCands : []);

  const backTxt = headerVM(s).backTxt;

  // `headerVM().scr` already falls back to `discover` when the game vanished,
  // so this branch is unreachable in the shipped frame — handled anyway. The
  // bar is drawn even here, and that matters more than it used to: the header
  // and the tab bar are gone on this screen, so a bodyless render without it
  // would be a dead full-screen rectangle with no way off it.
  if (!d)
    return (
      <div data-screen-label="Game detail" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <DetailTopBar backTxt={backTxt} onBack={() => s.goBack()} />
      </div>
    );

  const addBlocked = !d.inList && quickAddBlocked(d.id);

  const onListBtn = (): void => {
    if (!d.inList) {
      // PORTING-NOTES D-02, shared with the Discover ✚: a CheapShark-only row
      // has no appid the list FK can hold.
      if (addBlocked) return;
      void s.quickAdd(d.id);
      return;
    }
    // Arms first, ON BOTH TREES. The phone mirror calls `removeFromList`
    // straight from this press (:3088) and that is the riskier half of the
    // mirror rather than the authoritative one: this removes the game for the
    // WHOLE CREW, calls and comments with it, and every comparable control in
    // the app arms. Deliberate deviation from the mirror.
    if (!s.listRmArm) {
      s.setListRmArm(true);
      return;
    }
    s.setListRmArm(false);
    void s.removeFromList(d.id);
  };

  return (
    <div data-screen-label="Game detail" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <DetailTopBar
        backTxt={backTxt}
        onBack={() => s.goBack()}
        right={<FriendsToggle d={d} addBlocked={addBlocked} onOpen={() => s.openFriends()} />}
      />

      <div
        style={{
          // No `relative` here any more. It was load-bearing only for the
          // FRIENDS tab's `top:0`, and that control is on the bar now; the
          // cover's ribbons resolve against the cover's own box, not this one.
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          // The bottom used to be 26 with the tab bar's 82px spacer beneath it.
          // That spacer went with the tab bar, and so did the safe-area padding
          // the fixed bar carried (`MobileApp`), so the inset is claimed here
          // instead — otherwise the last row sits under the home indicator.
          padding: '14px 14px calc(26px + env(safe-area-inset-bottom))',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div style={{ fontSize: 23, fontWeight: 700, lineHeight: 1.12, color: '#fff' }}>{d.name}</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              fontFamily: MONO,
              fontSize: 9.5,
              lineHeight: 1.8,
              letterSpacing: '.04em',
              color: 'var(--gt-text-5)',
            }}
          >
            <span>{d.dev}</span>
            <span style={{ color: 'var(--gt-faint)' }}>·</span>
            <span>{d.platTxt}</span>
            <span style={{ color: 'var(--gt-faint)' }}>·</span>
            <span style={{ color: d.revClr }}>▮ {d.revTxt}</span>
            {d.hasAdded ? <span style={{ color: 'var(--gt-muted-2)' }}>· {d.addedTxt}</span> : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 14 }}>
          <div
            style={{
              position: 'relative',
              width: 150,
              aspectRatio: '2/3',
              flex: 'none',
              alignSelf: 'flex-start',
              background: d.fall,
              overflow: 'hidden',
              border: '1px solid var(--gt-border)',
              boxShadow: '5px 5px 0 rgba(0,0,0,.45)',
            }}
          >
            <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={d.ini} hasCover={art.hasCover} cover={art.cover} />
            {/* New on this tree. The three status pills under the price are gone,
                and the cover carries the same facts as ribbons — the art's own
                state rather than chrome laid over it. */}
            <Ribbon up={d.upcoming} ea={d.ea} demo={d.demo} />
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid var(--gt-border)',
              background: 'var(--gt-panel)',
            }}
          >
            {/* Opens the player-count editor (W5). Inert on a row with no appid,
                where the RPC could only answer UNKNOWN_GAME — the chevron beside
                the count follows the same gate, so an inert cell does not
                advertise a press. 56px minimum is the touch target. */}
            <div
              onClick={d.capAppid ? () => s.openCap(d.capAppid) : undefined}
              data-tip={d.capAppid ? undefined : NO_STEAM_TIP}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '10px 11px',
                background: 'var(--accSoft)',
                borderBottom: '1px solid var(--gt-line)',
                minHeight: 56,
                boxSizing: 'border-box',
                cursor: d.capAppid ? 'pointer' : 'default',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
                <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.1em', color: 'var(--gt-text-5)' }}>
                  PLAY TOGETHER
                </span>
                {/* The fit-summary third line is gone — the seat-cell grid already
                    says it — and tap-to-edit is signalled by this chevron beside
                    the count instead. The whole cell stays the tap target, so the
                    chevron follows the same `capAppid` gate the old ` ▸` did.
                    `design/GT Mobile Discover.dc.html` PLAY TOGETHER cell,
                    DESIGN-CHANGELOG 2026-08-13. Desktop keeps its fit line. */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-pixel)',
                      fontSize: 14,
                      color: 'var(--acc)',
                      transform: 'translateY(0.13em)',
                    }}
                  >
                    {d.capNum}
                  </span>
                  {d.capAppid ? (
                    <PixelTriangle w={10} h={11} rotate={90} style={{ color: 'var(--gt-muted)' }} />
                  ) : null}
                </span>
                {d.capNudge ? (
                  <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.05em', color: 'var(--gt-text-5)' }}>
                    {d.capNudge}
                  </span>
                ) : null}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, flex: 'none' }}>
                {d.seatCells.map((c) => (
                  <span
                    key={c.key}
                    style={{ width: 10, height: 10, boxSizing: 'border-box', background: c.bg, border: c.bd }}
                  />
                ))}
              </div>
            </div>

            <div style={ROW}>
              <span style={LABEL}>RELEASED</span>
              <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--gt-text)' }}>{d.dateFull}</span>
            </div>

            <div style={ROW}>
              <span style={LABEL}>PRICE</span>
              {d.hasOld ? (
                <span style={{ fontFamily: MONO, fontSize: 9.5, textDecoration: 'line-through', color: 'var(--gt-muted-2)' }}>
                  {d.oldTxt}
                </span>
              ) : null}
              <span style={{ fontFamily: MONO, fontSize: 13, color: 'var(--gt-text)' }}>{d.priceTxt}</span>
              {d.hasDisc ? (
                <span
                  style={{
                    fontFamily: MONO,
                    fontWeight: 700,
                    fontSize: 9.5,
                    background: 'var(--acc)',
                    color: 'var(--gt-ink)',
                    padding: '3px 5px',
                  }}
                >
                  {d.disc}
                </span>
              ) : null}
            </div>

            {/* Omitted rather than dashed when unknown — see `peakBare`. */}
            {d.hasPeak ? (
              <div style={ROW}>
                <span style={LABEL}>PEAK</span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--gt-text-3)' }}>{d.peakTxt}</span>
              </div>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '9px 11px' }}>
              <span style={LABEL}>TAGS</span>
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: 8.5,
                  lineHeight: 1.7,
                  letterSpacing: '.06em',
                  color: 'var(--gt-text-5)',
                }}
              >
                {d.tagsTxt}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            onClick={onListBtn}
            data-tip={addBlocked ? NO_STEAM_TIP : undefined}
            style={{
              border: '2px solid ' + (addBlocked ? 'var(--gt-border-2)' : s.listRmArm ? 'var(--acc2)' : d.listBtnBd),
              color: addBlocked ? 'var(--gt-muted-2)' : s.listRmArm ? 'var(--acc2)' : d.listBtnClr,
              background: addBlocked ? 'transparent' : s.listRmArm ? 'transparent' : d.listBtnBg,
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: 8.5,
              padding: '16px 12px',
              cursor: addBlocked ? 'not-allowed' : 'pointer',
              boxShadow: '3px 3px 0 rgba(0,0,0,.5)',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {d.inList && s.listRmArm ? 'SURE?' : d.listBtnTxt}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a
              href={d.buyUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--gt-border-2)',
                color: 'var(--gt-text-3)',
                fontFamily: MONO,
                fontSize: 10,
                height: 44,
                textDecoration: 'none',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
              }}
            >
              {d.buyTxt} ↗
            </a>
            {d.hasDemoBtn ? (
              <a
                href={d.demoUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--acc2)',
                  color: 'var(--acc2)',
                  fontFamily: MONO,
                  fontSize: 10,
                  height: 44,
                  textDecoration: 'none',
                  boxSizing: 'border-box',
                  whiteSpace: 'nowrap',
                }}
              >
                FREE DEMO ↗
              </a>
            ) : null}
          </div>
          {/* MY GAMES (DESIGN-CHANGELOG 2026-09-03; `design/GT Mobile Discover.dc.html`):
              two rows under the Steam row. First the backlog toggle and the
              bell — yours alone; the party sheet keeps only calls and
              comments. Then the score control and RECOMMEND ▸ (2026-09-12
              "Scoring is not a backlog privilege": the score row sits between
              the backlog/bell pair and RECOMMEND). */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div
              onClick={() => (d.soloOn ? void s.soloRm(d.id) : void s.soloAdd(d.id))}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                gap: 9,
                alignItems: 'center',
                justifyContent: 'center',
                height: 48,
                border: '1px solid var(--gt-border-2)',
                fontFamily: MONO,
                fontSize: 9.5,
                letterSpacing: '.06em',
                color: d.soloClr,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <svg
                viewBox="0 0 9 9"
                style={{ width: 14, height: 14, display: 'block', flex: 'none', color: d.soloOn ? 'var(--gt-text)' : 'var(--gt-text-5)' }}
                shapeRendering="crispEdges"
                aria-hidden="true"
              >
                <path d="M4 0h1v1H4zM3 1h1v1H3zM5 1h1v1H5zM2 2h1v1H2zM6 2h1v1H6zM1 3h1v1H1zM7 3h1v1H7zM0 4h1v1H0zM8 4h1v1H8zM1 5h1v1H1zM7 5h1v1H7zM2 6h1v1H2zM6 6h1v1H6zM3 7h1v1H3zM5 7h1v1H5zM4 8h1v1H4z" fill="currentColor" />
                {d.soloOn ? <path d="M4 3h1v1H4zM3 4h3v1H3zM4 5h1v1H4z" fill="var(--acc)" /> : null}
              </svg>
              <span>{d.soloTxt}</span>
            </div>
            {/* The bell (AA phase 4): 56×48, beside the backlog toggle. */}
            <div
              onClick={() => s.wOpen(d.id)}
              data-tip={d.bellTip}
              style={{
                width: 56,
                height: 48,
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                border: '1px solid ' + d.bellBd,
                color: d.bellClr,
                fontFamily: MONO,
                fontSize: 10,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <Bell on={d.bellOn} size={14} color={d.bellClr} />
              {d.bellOn ? <span>{d.bellN}</span> : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* The score control (DESIGN-CHANGELOG 2026-09-12; `design/GT Mobile
                Discover.dc.html:199`): 44px, beside RECOMMEND ▸. Opens the one
                YOUR SCORE modal for ANY game, shelved or not. */}
            <div
              onClick={() => s.openScore(d.id)}
              data-tip={d.scoreTip}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                gap: 7,
                alignItems: 'center',
                justifyContent: 'center',
                height: 48,
                padding: '0 10px',
                border: '1px solid ' + d.scoreBd,
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: '.05em',
                color: 'var(--gt-text-3)',
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <DoorIcon kind="bars" size={13} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.scoreTxt}</span>
              <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 13, color: d.scoreClr, display: 'flex', alignItems: 'center', flex: 'none' }}>
                <span style={{ transform: 'translateY(0.13em)' }}>{d.scoreNum}</span>
              </span>
            </div>
            {/* RECOMMEND ▸ (AA phase 3; `design/GT Mobile Discover.dc.html:199`) */}
            <div
              onClick={() => (d.sendOn ? s.sdOpen(d.id) : undefined)}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                gap: 9,
                alignItems: 'center',
                justifyContent: 'center',
                height: 48,
                border: '1px solid var(--gt-border-2)',
                fontFamily: MONO,
                fontSize: 9.5,
                letterSpacing: '.06em',
                color: d.sendOn ? 'var(--gt-text-2)' : 'var(--gt-text-5)',
                cursor: d.sendOn ? 'pointer' : 'default',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
              }}
            >
              <DoorIcon kind="hand" size={14} />
              <span>RECOMMEND ▸</span>
            </div>
          </div>
        </div>

        {/* Bare — V2 drops the MEDIA panel box around the carousel. */}
        <MediaCarousel gameId={d.id} />

        <div style={{ fontSize: 13.5, lineHeight: 1.7, color: 'var(--gt-text-3)', textWrap: 'pretty' }}>{d.descTxt}</div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            borderTop: '1px solid var(--gt-line)',
            paddingTop: 16,
          }}
        >
          {/* Content unchanged: a pending cell names the job it waits on and
              carries no number. The mirror draws all of these filled from seeded
              sample data, which is a seed rather than a spec. */}
          {d.stats.map((c) => (
            <div key={c.key} style={STAT_ROW}>
              <span style={{ ...LABEL, fontSize: 8.5, letterSpacing: '.11em' }}>{c.label}</span>
              {c.pending ? (
                <span
                  style={{
                    flex: '1 1 150px',
                    fontFamily: MONO,
                    fontSize: 8,
                    lineHeight: 1.7,
                    color: 'var(--gt-muted-2)',
                    textAlign: 'right',
                  }}
                >
                  {c.why}
                </span>
              ) : (
                <>
                  <span style={{ fontFamily: MONO, fontSize: 11.5, color: c.color }}>{c.value}</span>
                  <span style={{ fontFamily: MONO, fontSize: 8.5, color: 'var(--gt-muted-2)' }}>{c.caption}</span>
                </>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
                PLAYERS ONLINE · 90D
              </span>
              <div style={{ flex: 1 }} />
              {d.nowTxt ? (
                <span style={{ ...CHIP, color: 'var(--gt-text-3)', border: '1px solid var(--gt-border-2)' }}>
                  {d.nowTxt}
                </span>
              ) : null}
              {d.trackedTxt ? (
                <span style={{ ...CHIP, color: 'var(--gt-muted-2)', border: '1px dashed var(--gt-border-2)' }}>
                  {d.trackedTxt}
                </span>
              ) : null}
              {d.chartIsSample ? (
                <span style={{ ...CHIP, color: 'var(--gt-muted-2)', border: '1px dashed var(--gt-border-2)' }}>
                  SAMPLE
                </span>
              ) : null}
            </div>
            {d.hasChart ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 70 }}>
                  {d.bars.map((b) => (
                    <div key={b.key} style={{ flex: 1, height: b.h, background: b.c }} />
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    fontFamily: MONO,
                    fontSize: 8,
                    letterSpacing: '.05em',
                    color: 'var(--gt-muted-2)',
                  }}
                >
                  <span>90D AGO</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--gt-line)' }} />
                  <span>TODAY</span>
                </div>
              </>
            ) : null}
            {d.noChart ? (
              <div
                style={{
                  height: 70,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed var(--gt-border)',
                  fontFamily: MONO,
                  fontSize: 10,
                  color: 'var(--gt-muted-2)',
                  textAlign: 'center',
                  padding: '0 16px',
                  lineHeight: 1.8,
                }}
              >
                NO PLAYER DATA YET — {d.noChartWhy}
              </div>
            ) : null}
          </div>

          {/* PRICE · 12 MONTHS (migration 3045). Its own block rather than a band
              cell, matching the design and the players chart above: a sparkline
              plus a chip does not fit a value/caption pair. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
                PRICE · 12 MONTHS
              </span>
              <div style={{ flex: 1 }} />
              {/* The LOW chip carries the accent, because "it has been cheaper"
                  is the one thing a reader is here to find out. Absent entirely
                  until the price has actually moved — see `mkPriceBars`. */}
              {d.hasPriceLow ? (
                <span style={{ ...CHIP, color: 'var(--acc)', border: '1px solid var(--acc)' }}>
                  LOW {d.priceLowTxt} · {d.priceLowWhen}
                </span>
              ) : null}
              {d.priceIsSample ? (
                <span style={{ ...CHIP, color: 'var(--gt-muted-2)', border: '1px dashed var(--gt-border-2)' }}>
                  SAMPLE
                </span>
              ) : null}
            </div>
            {d.hasPriceChart ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 44 }}>
                  {d.priceBars.map((b) => (
                    <div key={b.key} style={{ flex: 1, height: b.h, background: b.c }} />
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    fontFamily: MONO,
                    fontSize: 8,
                    letterSpacing: '.05em',
                    color: 'var(--gt-muted-2)',
                  }}
                >
                  <span>12M AGO</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--gt-line)' }} />
                  <span>{d.priceFoot}</span>
                </div>
              </>
            ) : (
              <div
                style={{
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed var(--gt-border)',
                  fontFamily: MONO,
                  fontSize: 10,
                  color: 'var(--gt-muted-2)',
                  textAlign: 'center',
                  padding: '0 16px',
                  lineHeight: 1.8,
                }}
              >
                {d.noPriceChartWhy}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
